// =============================================================================
// domain/readiness.ts — the pre-market readiness gate.
// -----------------------------------------------------------------------------
// A 12-item checklist in three groups, and the one thing on the Pre-Market tab
// that answers "did I complete my own pre-flight".
//
// ── 🚨 THE ITEM DEFINITIONS AND THE TICKS LIVE IN DIFFERENT TABLES ──
//
//   user_preferences.readiness_groups   WHICH items exist, their labels, order
//   journal_entries.readiness_items     WHETHER each was ticked, per day
//   journal_entries.readiness_completed_at   when the last one landed
//
// That split is the old client's and it is load-bearing: the gear beside the
// gate edits a GLOBAL list, and every historical day's ticks are keyed by the
// stable item key. Renaming an item preserves history; removing one leaves
// orphaned keys in old entries, which is harmless because completeness is
// computed against the ACTIVE list only.
//
// ⚠ Consequence for this client: the checklist MUST be rendered from the stored
// preference, never from `DEFAULT_READINESS_GROUPS`. Josh's stored copy happens
// to match the defaults today — so a hardcoded version would look correct and
// silently diverge the first time anyone touches the gear in beta. That is the
// D61 shape: a default that renders plausibly is worse than a blank.
// =============================================================================

export interface ReadinessItem {
  key: string;
  label: string;
}

export interface ReadinessGroup {
  key: string;
  label: string;
  items: ReadinessItem[];
}

/**
 * The seed list, transcribed from the old client's `READINESS_GROUPS`.
 *
 * ⚠ A FALLBACK, not the source of truth. The old client seeds
 * `settings.readinessGroups` from this on first load and reads the setting from
 * then on (`getReadinessGroups`), falling back here only when the stored array is
 * missing or empty. `readinessGroupsOrDefault` reproduces exactly that rule.
 */
export const DEFAULT_READINESS_GROUPS: readonly ReadinessGroup[] = [
  {
    key: 'market',
    label: 'Market Readiness',
    items: [
      { key: 'keyLevelsMarked', label: 'Key levels marked' },
      { key: 'newsCalendarChecked', label: 'News calendar checked' },
      { key: 'priorDayCloseReviewed', label: 'Prior-day close reviewed' },
      { key: 'marketRegimeIdentified', label: 'Market regime identified' },
    ],
  },
  {
    key: 'account',
    label: 'Account Readiness',
    items: [
      { key: 'accountSelected', label: 'Account selected for today' },
      { key: 'ddChecked', label: 'Distance to drawdown checked' },
      { key: 'maxRiskSet', label: "Today's max risk set" },
      { key: 'noPendingViolations', label: 'No pending rule violations' },
    ],
  },
  {
    key: 'platform',
    label: 'Platform Readiness',
    items: [
      { key: 'platformReloaded', label: 'Platform Reload Before Open' },
      { key: 'platformAccountSelected', label: 'Proper Account Selected' },
      { key: 'instrumentsSelected', label: 'Proper Instruments Selected' },
      { key: 'crossTradingEnabled', label: 'Cross-Trading Enabled' },
    ],
  },
];

/**
 * The stored groups, or the seed list when there are none.
 *
 * ⚠ `length === 0` falls back too, matching `getReadinessGroups`. An empty array
 * is what a user who deleted every item would store, and the old client shows
 * them the defaults again rather than an empty gate — so this client must not
 * "helpfully" render nothing.
 *
 * Parsed defensively because the column is `unknown[]`: a group with no items
 * array, or an item with no key, would otherwise make the gate uncheckable or
 * make `isReadinessComplete` throw.
 */
export function readinessGroupsOrDefault(stored: unknown): readonly ReadinessGroup[] {
  if (!Array.isArray(stored) || stored.length === 0) return DEFAULT_READINESS_GROUPS;
  const out: ReadinessGroup[] = [];
  for (const g of stored) {
    if (!g || typeof g !== 'object') continue;
    const gg = g as Record<string, unknown>;
    const rawItems = Array.isArray(gg['items']) ? (gg['items'] as unknown[]) : [];
    const items: ReadinessItem[] = [];
    for (const it of rawItems) {
      if (!it || typeof it !== 'object') continue;
      const ii = it as Record<string, unknown>;
      const key = typeof ii['key'] === 'string' ? ii['key'] : '';
      if (!key) continue;
      // ⚠ Falls back to the KEY, never to ''. An unlabelled item still has to be
      // tickable and identifiable, and a blank row in a checklist is unusable.
      items.push({ key, label: typeof ii['label'] === 'string' && ii['label'] ? ii['label'] : key });
    }
    out.push({
      key: typeof gg['key'] === 'string' ? gg['key'] : '',
      label: typeof gg['label'] === 'string' ? gg['label'] : '',
      items,
    });
  }
  // Every group unparseable — treat as absent rather than rendering an empty gate.
  return out.some((g) => g.items.length) ? out : DEFAULT_READINESS_GROUPS;
}

/** Was this item ticked? Anything but a literal `true` counts as unticked. */
export function isTicked(items: Readonly<Record<string, unknown>>, key: string): boolean {
  return items[key] === true;
}

/**
 * Is EVERY active item ticked?
 *
 * ⚠ COMPUTED, never read off `completedAt`. The two can disagree, and the
 * dangerous direction is real: tick all twelve (stamping `completedAt`), then add
 * a thirteenth item with the gear — the old client immediately reports the gate
 * incomplete, because it walks the ACTIVE list. A surface reading `completedAt`
 * would keep telling the trader they were ready.
 *
 * ⚠ An empty active list is NOT complete. "You have no checklist" must not read
 * as "your checklist is satisfied" on the pill that gates the session.
 */
export function isReadinessComplete(
  items: Readonly<Record<string, unknown>>,
  groups: readonly ReadinessGroup[],
): boolean {
  let seen = 0;
  for (const g of groups) {
    for (const it of g.items) {
      seen++;
      if (!isTicked(items, it.key)) return false;
    }
  }
  return seen > 0;
}

/** How many of the active items are ticked, and out of how many. */
export function readinessProgress(
  items: Readonly<Record<string, unknown>>,
  groups: readonly ReadinessGroup[],
): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const g of groups) {
    for (const it of g.items) {
      total++;
      if (isTicked(items, it.key)) done++;
    }
  }
  return { done, total };
}

export interface ReadinessToggle {
  items: Record<string, boolean>;
  completedAt: string | null;
}

/**
 * Apply one tick, and decide what happens to `completedAt`.
 *
 * Ported literally from the old client's checkbox handler:
 *
 *     incomplete → complete   stamp now
 *     complete   → incomplete clear to null
 *     otherwise               leave it exactly as it was
 *
 * ⚠ That third case is the one worth stating. Un-ticking one of twelve when two
 * were already unticked must NOT clear a stamp — there is no stamp to clear, and
 * writing `null` over an existing one would erase the record of a day the trader
 * did complete their pre-flight before changing their mind about one box.
 *
 * ⚠ `previousCompletedAt` is carried through rather than recomputed, because a
 * stamp is a historical fact about when the gate closed, not a function of the
 * current tick state.
 */
export function applyReadinessToggle(
  current: Readonly<Record<string, unknown>>,
  key: string,
  checked: boolean,
  groups: readonly ReadinessGroup[],
  previousCompletedAt: string | null,
  nowIso: string,
): ReadinessToggle {
  const wasComplete = isReadinessComplete(current, groups);

  // ⚠ Normalised to real booleans. The column is JSONB and the old client stores
  // `false` explicitly (unlike the trade breach flags, which DELETE a false), so
  // an unticked item is `false` here and not an absent key.
  const items: Record<string, boolean> = {};
  for (const g of groups) for (const it of g.items) items[it.key] = isTicked(current, it.key);
  // ⚠ Orphans from removed items are carried, not dropped — the old client leaves
  // them in place, and a later re-add of the same key should find its history.
  for (const k of Object.keys(current)) if (!(k in items)) items[k] = current[k] === true;
  items[key] = checked;

  const nowComplete = isReadinessComplete(items, groups);
  if (!wasComplete && nowComplete) return { items, completedAt: nowIso };
  if (wasComplete && !nowComplete) return { items, completedAt: null };
  return { items, completedAt: previousCompletedAt };
}
